import { create } from "zustand";

// ============================================================
// Athena Chatbot Store
//
// Controls the chatbot's visibility and which MAWB is loaded.
// Call openAthena(mawb) from anywhere — the search box,
// a Master View row click, a button, a keyboard shortcut, etc.
//
// Usage:
//   import { useAthenaStore } from "./useAthenaStore";
//
//   // Open from any component:
//   const openAthena = useAthenaStore((s) => s.openAthena);
//   openAthena("05755912544");
//
//   // Or outside React (e.g. in a utility or event handler):
//   import { useAthenaStore } from "./useAthenaStore";
//   useAthenaStore.getState().openAthena("05755912544");
// ============================================================

interface AthenaState {
    // State
    isOpen: boolean;
    mawb: string | null;
    sessionKey: string | null;

    // Actions
    openAthena: (mawb: string) => void;
    closeAthena: () => void;
    minimizeAthena: () => void;
    restoreAthena: () => void;
    setSessionKey: (key: string | null) => void;
}

export const useAthenaStore = create<AthenaState>((set, get) => ({
    // Initial state — closed, no MAWB loaded
    isOpen: false,
    mawb: null,
    sessionKey: null,

    // Open the chatbot for a specific MAWB.
    // If already open with a different MAWB, it switches.
    openAthena: (mawb: string) => {
        const current = get();
        const cleanMawb = mawb.replace(/[-\s]/g, "").trim();

        if (current.mawb === cleanMawb && current.isOpen) {
            // Already open for this MAWB — just ensure visible
            return;
        }

        set({
            isOpen: true,
            mawb: cleanMawb,
            // Reset session key so the component knows to re-initialize
            sessionKey: current.mawb !== cleanMawb ? null : current.sessionKey,
        });
    },

    // Close and fully reset (ends the session)
    closeAthena: () =>
        set({
            isOpen: false,
            mawb: null,
            sessionKey: null,
        }),

    // Minimize — hide the panel but keep the session alive
    minimizeAthena: () => set({ isOpen: false }),

    // Restore a minimized session
    restoreAthena: () => {
        const { mawb } = get();
        if (mawb) set({ isOpen: true });
    },

    // Called by the chat component after successful initialization
    setSessionKey: (key: string | null) => set({ sessionKey: key }),
}));